import { mapThemes } from "@/assets/maputnik_themes/themes";
import { SiteHeader } from "@/components/site-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMapWs, type NeighborUser } from "@/hooks/use-map-ws";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { useEffect, useRef, useState } from "react";

const PMTILES_URL = process.env.BUN_PUBLIC_PMTILES_URL!;
const USER_TTL_MS = 10_000;
const SMOOTH_MS = 333;

// ── Style patching ────────────────────────────────────────────────────────────
const pmtilesSource = {
  type: "vector" as const,
  url: `pmtiles://${PMTILES_URL}`,
  attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
};

const themes = mapThemes.map((t) => {
  const originalSourceNames = Object.keys(t.style.sources ?? {});
  const layers = (t.style.layers ?? []).map((layer) => {
    if (
      "source" in layer &&
      originalSourceNames.includes(layer.source as string)
    ) {
      return { ...layer, source: "openmaptiles" };
    }
    return layer;
  });
  return {
    ...t,
    style: {
      ...t.style,
      glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
      sprite: "https://maputnik.github.io/osm-liberty/sprites/osm-liberty",
      sources: { openmaptiles: pmtilesSource },
      layers,
    },
  };
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
) {
  const R = 6_371_000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function createArrowEl(_clubed: boolean, heading: number) {
  const el = document.createElement("div");
  el.style.cssText =
    "width:16px;height:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 0 2px rgba(0,0,0,0.8))";
  const color = "#ffffff";
  el.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="${color}" stroke="white" stroke-width="1.5" style="transform:rotate(${heading}deg);transition:none"><polygon points="12,2 22,20 12,16 2,20"/></svg>`;
  return el;
}

function createLabelEl(username: string, clubName?: string | null) {
  const el = document.createElement("div");
  el.style.cssText =
    "background:transparent;border:none;box-shadow:none;white-space:nowrap;pointer-events:none";
  el.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:2px;margin-top:4px">
      <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:11px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4">${username}</div>
      ${clubName ? `<div style="background:rgba(21,128,61,0.85);color:#bbf7d0;font-size:10px;font-family:monospace;padding:1px 5px;border-radius:3px;line-height:1.4">${clubName}</div>` : ""}
    </div>`;
  return el;
}

type AnimatedMarker = maplibregl.Marker & { __animId?: number };

function animateMarker(
  marker: AnimatedMarker,
  to: { lat: number; lng: number },
  duration: number,
) {
  if (marker.__animId) cancelAnimationFrame(marker.__animId);
  const from = marker.getLngLat();
  const start = Date.now();
  const tick = () => {
    const t = Math.min((Date.now() - start) / duration, 1);
    const ease = 1 - (1 - t) ** 3;
    marker.setLngLat([
      from.lng + (to.lng - from.lng) * ease,
      from.lat + (to.lat - from.lat) * ease,
    ]);
    if (t < 1) {
      marker.__animId = requestAnimationFrame(tick);
    } else {
      marker.setLngLat([to.lng, to.lat]);
    }
  };
  marker.__animId = requestAnimationFrame(tick);
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface MarkerEntry {
  dot: AnimatedMarker;
  label: AnimatedMarker;
}

interface UserEntry extends NeighborUser {
  _ttl: number;
}

// ── Component ─────────────────────────────────────────────────────────────────
export function LiveMapPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [selectedId, setSelectedId] = useState(themes[0]?.id ?? "");
  const [cursor, setCursor] = useState<{ lat: number; lng: number } | null>(
    null,
  );

  const usersRef = useRef<Map<string, UserEntry>>(new Map());
  const markersRef = useRef<Map<string, MarkerEntry>>(new Map());

  function removeMarker(key: string) {
    const m = markersRef.current.get(key);
    if (!m) return;
    m.dot.remove();
    m.label.remove();
    markersRef.current.delete(key);
  }

  function upsertMarker(u: NeighborUser) {
    const map = mapRef.current;
    if (!map) return;
    const key = String(u.user_id);
    const clubed = !!u.current_club_name;

    if (markersRef.current.has(key)) {
      const m = markersRef.current.get(key)!;
      animateMarker(m.dot, { lat: u.lat, lng: u.lng }, SMOOTH_MS);
      animateMarker(m.label, { lat: u.lat, lng: u.lng }, SMOOTH_MS);
      const svg = m.dot.getElement().querySelector("svg");
      if (svg) {
        svg.setAttribute(
          "style",
          `transform:rotate(${u.heading}deg);transition:none`,
        );
        svg.setAttribute("fill", "#ffffff");
      }
      const inner = m.label.getElement().querySelector("div");
      if (inner) {
        inner.innerHTML = `
          <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:11px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4">${u.username ?? key}</div>
          ${u.current_club_name ? `<div style="background:rgba(21,128,61,0.85);color:#bbf7d0;font-size:10px;font-family:monospace;padding:1px 5px;border-radius:3px;line-height:1.4">${u.current_club_name}</div>` : ""}`;
      }
    } else {
      const dot = new maplibregl.Marker({
        element: createArrowEl(clubed, u.heading),
      })
        .setLngLat([u.lng, u.lat])
        .addTo(map) as AnimatedMarker;
      const label = new maplibregl.Marker({
        element: createLabelEl(u.username ?? key, u.current_club_name),
        anchor: "top",
      })
        .setLngLat([u.lng, u.lat])
        .addTo(map) as AnimatedMarker;
      markersRef.current.set(key, { dot, label });
    }
  }

  function handleNeighborUpdate(payload: NeighborUser[]) {
    const ttl = Date.now() + USER_TTL_MS;
    payload.forEach((u) => {
      usersRef.current.set(String(u.user_id), { ...u, _ttl: ttl });
      upsertMarker(u);
    });
  }

  const { status: wsStatus, sendViewportUpdate } = useMapWs({
    onNeighborUpdate: handleNeighborUpdate,
  });

  function sendCurrentViewport() {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const ne = map.getBounds().getNorthEast();
    const radius = haversineMeters(
      { lat: center.lat, lng: center.lng },
      { lat: ne.lat, lng: ne.lng },
    );
    sendViewportUpdate(center.lat, center.lng, radius);
  }

  // Send viewport when WS connects
  useEffect(() => {
    if (wsStatus === "connected") sendCurrentViewport();
  }, [wsStatus]);

  useEffect(() => {
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);

    const initial = themes.find((t) => t.id === selectedId) ?? themes[0];
    const map = new maplibregl.Map({
      container: containerRef.current!,
      style: initial.style,
      center: [0, 20],
      zoom: 2,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.addControl(new maplibregl.ScaleControl(), "bottom-left");
    map.on("mousemove", (e) =>
      setCursor({ lat: e.lngLat.lat, lng: e.lngLat.lng }),
    );
    map.on("mouseleave", () => setCursor(null));
    map.on("moveend", sendCurrentViewport);

    mapRef.current = map;

    const evictInterval = setInterval(() => {
      const now = Date.now();
      usersRef.current.forEach((u, key) => {
        if (u._ttl < now) {
          usersRef.current.delete(key);
          removeMarker(key);
        }
      });
    }, 1_000);

    return () => {
      clearInterval(evictInterval);
      markersRef.current.forEach((_, key) => removeMarker(key));
      map.remove();
      mapRef.current = null;
      maplibregl.removeProtocol("pmtiles");
    };
  }, []);

  function handleThemeChange(id: string) {
    setSelectedId(id);
    const theme = themes.find((t) => t.id === id);
    if (theme && mapRef.current) mapRef.current.setStyle(theme.style);
  }

  return (
    <>
      <SiteHeader
        title="Live Map"
        status={
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className={`size-2 rounded-full ${
                wsStatus === "connected"
                  ? "bg-green-500"
                  : wsStatus === "connecting"
                    ? "bg-yellow-500 animate-pulse"
                    : "bg-red-500"
              }`}
            />
            <span>
              {wsStatus === "connected"
                ? "Live"
                : wsStatus === "connecting"
                  ? "Connecting…"
                  : "Disconnected"}
            </span>
          </div>
        }
      />
      <div className="flex flex-1 flex-col overflow-hidden relative">
        <div ref={containerRef} style={{ flex: 1 }} />
        {cursor && (
          <div className="absolute bottom-8 right-3 z-10 flex gap-3 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-1.5 text-xs font-mono text-muted-foreground">
            <span>Lat: {cursor.lat.toFixed(5)}</span>
            <span>Lng: {cursor.lng.toFixed(5)}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 z-10">
          <Select value={selectedId} onValueChange={handleThemeChange}>
            <SelectTrigger className="w-48 bg-background/90 backdrop-blur-sm shadow-md">
              <SelectValue placeholder="Select theme" />
            </SelectTrigger>
            <SelectContent>
              {themes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
}
